import React from 'react';
import UserAvatar from '../profile/UserAvatar';
import { formatDistanceToNow } from 'date-fns';
import { formatRating, formatScore } from '../../utils/format';

interface LeaderboardEntryProps {
  rank: number;
  username: string;
  iconUrl?: string | null;
  score: number;
  achievedAt: string;
  isTopThree?: boolean;
  isCurrentUser?: boolean;
  formatAsRating?: boolean;
}

const LeaderboardEntry: React.FC<LeaderboardEntryProps> = ({
  rank,
  username,
  iconUrl,
  score,
  achievedAt,
  isTopThree = false,
  isCurrentUser = false,
  formatAsRating = false
}) => {
  const getCrownEmoji = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const crown = getCrownEmoji(rank);

  const getBackgroundColor = () => {
    if (rank === 1) return 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800';
    if (rank === 2) return 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700';
    if (rank === 3) return 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800';
    return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const formatted = formatDistanceToNow(date, { addSuffix: true });
      return formatted || dateString;
    } catch {
      return dateString;
    }
  };

  const formattedScore = formatAsRating ? formatRating(score) : formatScore(score);
  const scoreLabel = formatAsRating ? 'rating' : 'score';

  return (
    <div className={`flex items-center justify-between p-4 rounded-lg border ${getBackgroundColor()} ${isCurrentUser ? 'border-l-4 border-l-mango-500' : ''} transition-all hover:shadow-md`}>
      <div className="flex items-center space-x-4 flex-1">
        {/* Rank */}
        <div className="flex items-center space-x-2">
          {crown && <span className="text-2xl">{crown}</span>}
          <span className={`text-lg font-bold ${isTopThree ? 'text-mango-600 dark:text-mango-400' : 'text-gray-600 dark:text-gray-400'} min-w-[2rem] text-center`}>
            {rank}
          </span>
        </div>

        {/* User Avatar */}
        <UserAvatar
          iconUrl={iconUrl}
          username={username}
          size="md"
        />

        {/* Username */}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {username}
            </span>
            {isCurrentUser && (
              <span className="px-2 py-0.5 text-xs font-bold bg-mango-500 text-white rounded">
                YOU
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(achievedAt)}
          </span>
        </div>
      </div>

      {/* Score */}
      <div className="flex flex-col items-end ml-4">
        <span className={`text-2xl font-bold ${isTopThree ? 'text-mango-600 dark:text-mango-400' : 'text-gray-900 dark:text-white'}`}>
          {formattedScore}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {scoreLabel}
        </span>
      </div>
    </div>
  );
};

export default LeaderboardEntry;
